// Hub global de anotações — vê todas as notas do aluno cross-target.
// [[project-notes-system-caderno]] — hub cross-caderno.
//
// Cada nota mostra o "contexto" (caderno ou banco de origem). Click numa
// nota abre um modal de preview com botão "Abrir contexto" pra deeplink.

import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowRight,
  BookOpen,
  ChevronRight,
  Database,
  ExternalLink,
  FileDown,
  HelpCircle,
  Loader2,
  MoreVertical,
  Pencil,
  PictureInPicture2,
  Pin,
  Search,
  Share2,
  StickyNote,
  Tag,
  Trash2,
  X
} from "lucide-react";
import { call } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";
import {
  exportSingleNotePdf,
  noteContentToHtml,
  noteContentToPreview
} from "./CadernoNotesPanel";
import { activeNav } from "./App";
import { openBank } from "./BankDetailView";
import { activeSessionId } from "./AiStudyView";
import { openShareDialog } from "./ShareDialog";
import { openPrompt, openAlert } from "./ConfirmPromptModal";
import {
  openNoteInWindow,
  useOpenNoteWindowIds,
  useSingleNoteWindowOpen
} from "./SingleNoteWindow";

interface HubNote {
  id: string;
  targetKind: "ai_study_session" | "bank" | "question";
  targetId: string;
  /** Nome do caderno/banco onde a nota mora — null se o alvo foi apagado
   *  ou se é uma nota de questão (sem schema pra parent-bank ainda). */
  targetTitle: string | null;
  title: string;
  contentMd: string;
  tags: string[];
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
}

export function AnotacoesHubView() {
  const [notes, setNotes] = useState<HubNote[]>([]);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [open, setOpen] = useState<HubNote | null>(null);
  // Toggle "mostrar abertas em janela". Por padrão FALSE — quem tá em janela
  // some do grid pra não tomar espaço. Aluno liga pra ver tudo de novo.
  const [showOpenInWindow, setShowOpenInWindow] = useState(false);
  const openInWindowIds = useOpenNoteWindowIds();

  async function load() {
    setPhase("loading");
    setErr(null);
    try {
      const res = await call<{ notes: HubNote[] }>(
        "/api/student/notes?all=1"
      );
      setNotes(res.notes);
      setPhase("ready");
    } catch (e) {
      setErr(friendlyError(e));
      setPhase("error");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Quando o pop-up de UMA nota fechar, recarrega o hub pra refletir edições.
  // BroadcastChannel "note-window" tem eventos open/heartbeat/close.
  useEffect(() => {
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel("note-window");
      bc.onmessage = (ev) => {
        const data = ev.data as { event?: string };
        if (data?.event === "close") void load();
      };
    } catch {
      /* sem BroadcastChannel */
    }
    return () => {
      if (bc) bc.close();
    };
  }, []);

  // Rename: PATCH /api/student/notes?id=… { title }. Atualiza estado local
  // sem refetch (otimista). Em caso de erro, recarrega da fonte da verdade.
  async function renameNote(id: string, currentTitle: string): Promise<void> {
    const newTitle = await openPrompt({
      title: "Renomear anotação",
      message: "Digite o novo título.",
      defaultValue: currentTitle,
      placeholder: "Título da anotação",
      confirmLabel: "Salvar",
      maxLength: 200
    });
    if (newTitle == null) return;
    const trimmed = newTitle.trim();
    try {
      const res = await call<{ note: HubNote }>(
        `/api/student/notes?id=${encodeURIComponent(id)}`,
        { method: "PATCH", body: JSON.stringify({ title: trimmed }) }
      );
      setNotes((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, title: res.note.title ?? trimmed } : n
        )
      );
    } catch (e) {
      await openAlert({
        title: "Não foi possível renomear",
        message: friendlyError(e),
        danger: true
      });
    }
  }

  async function deleteNote(id: string, title: string): Promise<void> {
    const echo = title?.trim() ? ` "${title}"` : "";
    const typed = await openPrompt({
      title: `Apagar anotação${echo}?`,
      message:
        "Essa ação é permanente — a anotação será removida e não pode ser recuperada. Para confirmar, digite APAGAR abaixo.",
      placeholder: "APAGAR",
      confirmLabel: "Apagar definitivamente",
      requirePhrase: "APAGAR",
      danger: true
    });
    if (typed == null) return;
    try {
      await call(`/api/student/notes?id=${encodeURIComponent(id)}`, {
        method: "DELETE"
      });
      setNotes((prev) => prev.filter((n) => n.id !== id));
    } catch (e) {
      await openAlert({
        title: "Não foi possível apagar",
        message: friendlyError(e),
        danger: true
      });
    }
  }

  // Filtro client-side: substring no título, conteúdo e tags + filtro de tag.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return notes.filter((n) => {
      // Filtra notas abertas em janela quando o toggle está desligado —
      // assim o grid não fica com slots ocupados por algo que já tem foco
      // numa janela avulsa. Aluno liga o toggle pra ver tudo de novo.
      if (!showOpenInWindow && openInWindowIds.has(n.id)) return false;
      if (activeTag && !n.tags.includes(activeTag)) return false;
      if (!q) return true;
      // Busca no texto puro (sem tags HTML / markdown) pra não casar com
      // pedaços de `<p>` ou `<br>` no conteúdo. [[project-notes-system-caderno]]
      const hay =
        n.title.toLowerCase() +
        " " +
        noteContentToPreview(n.contentMd, 4000).toLowerCase() +
        " " +
        n.tags.join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [notes, search, activeTag, showOpenInWindow, openInWindowIds]);

  const hiddenInWindowCount = useMemo(
    () => notes.filter((n) => openInWindowIds.has(n.id)).length,
    [notes, openInWindowIds]
  );

  // Topics: top-20 tags mais usadas (com contagem).
  const topTags = useMemo(() => {
    const map = new Map<string, number>();
    for (const n of notes) {
      for (const t of n.tags) {
        map.set(t, (map.get(t) ?? 0) + 1);
      }
    }
    return [...map.entries()]
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);
  }, [notes]);

  return (
    <div class="space-y-4">
      <header>
        <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <StickyNote className="size-6 text-primary" />
          Minhas anotações
        </h1>
        <p class="mt-1 text-sm text-muted-foreground">
          Tudo o que você anotou — cadernos, bancos e questões. Busca dentro
          do texto e por tag.
        </p>
      </header>

      <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div class="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
            placeholder="Buscar no título, tags ou texto interno…"
            aria-label="Buscar anotações"
            class="w-full rounded-md border border-border bg-background py-2 pl-9 pr-9 text-sm outline-none focus:border-primary"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Limpar"
              class="absolute right-2 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
        {hiddenInWindowCount > 0 && (
          <button
            type="button"
            onClick={() => setShowOpenInWindow((v) => !v)}
            class={`inline-flex shrink-0 items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
              showOpenInWindow
                ? "border-primary bg-primary/10 text-primary"
                : "border-border bg-background text-muted-foreground hover:bg-accent"
            }`}
            title={
              showOpenInWindow
                ? "Esconder cards de anotações que já estão em janela"
                : "Mostrar cards das anotações que estão em janela"
            }
          >
            <PictureInPicture2 className="size-3.5" />
            {showOpenInWindow ? "Ocultar abertas" : `Mostrar ${hiddenInWindowCount} em janela`}
          </button>
        )}
      </div>

      {topTags.length > 0 && (
        <div class="flex flex-wrap items-center gap-1.5">
          <span class="mr-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <Tag class="mr-1 inline size-3" />
            Tags:
          </span>
          <button
            type="button"
            onClick={() => setActiveTag(null)}
            class={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
              activeTag === null
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-card/60 text-muted-foreground hover:border-primary/40 hover:text-foreground"
            }`}
          >
            Todas
          </button>
          {topTags.map((t) => {
            const isActive = activeTag === t.tag;
            return (
              <button
                key={t.tag}
                type="button"
                onClick={() => setActiveTag(isActive ? null : t.tag)}
                class={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                  isActive
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card/60 text-muted-foreground hover:border-primary/40 hover:text-foreground"
                }`}
              >
                {t.tag}
                <span
                  class={`rounded-full px-1 text-[9px] font-bold ${
                    isActive
                      ? "bg-primary-foreground text-primary"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {phase === "loading" && (
        <p class="flex items-center gap-2 py-12 text-center text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando…
        </p>
      )}
      {phase === "error" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="mr-2 inline size-4" />
          {err}
        </div>
      )}
      {phase === "ready" && filtered.length === 0 && (
        <div class="rounded-md border border-dashed border-border bg-background/40 px-4 py-12 text-center">
          <StickyNote className="mx-auto mb-3 size-8 text-muted-foreground" />
          <p class="text-sm text-muted-foreground">
            {search || activeTag
              ? "Nenhuma anotação bate com a busca."
              : "Você ainda não criou anotações. Crie uma dentro de um caderno ou banco."}
          </p>
        </div>
      )}

      {filtered.length > 0 && (
        <div class="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((n) => (
            <NoteCard
              key={n.id}
              note={n}
              onOpen={() => setOpen(n)}
              onRename={() => void renameNote(n.id, n.title)}
              onDelete={() => void deleteNote(n.id, n.title)}
            />
          ))}
        </div>
      )}

      {open && <NotePreviewModal note={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function NoteCard({
  note,
  onOpen,
  onRename,
  onDelete
}: {
  note: HubNote;
  onOpen: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  const preview = noteContentToPreview(note.contentMd, 220);
  const [menuOpen, setMenuOpen] = useState(false);
  // Quando a nota está aberta numa janela avulsa, desabilita o card (clique
  // tenta focar a janela em vez de abrir o preview inline, e o menu fica
  // travado pra evitar edição concorrente). [[project-notes-hub-popout]]
  const openInWindow = useSingleNoteWindowOpen(note.id);
  function focusOrReopen() {
    // window.open com mesmo "name" foca a janela existente em vez de duplicar.
    openNoteInWindow(note.id);
  }
  return (
    <div
      class={`relative flex flex-col rounded-lg border bg-card/40 p-4 transition-colors ${
        openInWindow
          ? "border-primary/40"
          : "border-border hover:border-primary/40"
      }`}
    >
      {/* Path/breadcrumb clicável — mostra ONDE a nota mora. */}
      <div class="flex items-start justify-between gap-2">
        <NoteContextPath note={note} />
        <NoteCardMenu
          open={menuOpen}
          disabled={openInWindow}
          onToggle={() => setMenuOpen((v) => !v)}
          onClose={() => setMenuOpen(false)}
          onRename={() => {
            setMenuOpen(false);
            onRename();
          }}
          onShare={() => {
            setMenuOpen(false);
            openShareDialog({
              kind: "nota",
              resourceId: note.id,
              resourceTitle: note.title || "(sem título)"
            });
          }}
          onPopOut={() => {
            setMenuOpen(false);
            openNoteInWindow(note.id);
          }}
          onPdf={() => {
            setMenuOpen(false);
            void exportSingleNotePdf(note.id);
          }}
          onDelete={() => {
            setMenuOpen(false);
            onDelete();
          }}
        />
      </div>
      <button
        type="button"
        onClick={openInWindow ? focusOrReopen : onOpen}
        class="mt-2 flex flex-col text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
        aria-label={
          openInWindow
            ? `Anotação aberta em janela · clique pra trazer pra frente`
            : `Abrir anotação ${note.title || "sem título"}`
        }
      >
        <div class="mb-1 flex items-start justify-between gap-2">
          <h3 class="line-clamp-2 text-sm font-bold leading-snug">
            {note.title || "(sem título)"}
          </h3>
          {note.pinned && (
            <Pin className="mt-0.5 size-3 shrink-0 text-amber-500" />
          )}
        </div>
        <p class="line-clamp-3 text-[11px] text-muted-foreground">
          {preview || "(vazia)"}
        </p>
      </button>
      {openInWindow && (
        <div class="mt-2 inline-flex w-fit items-center gap-1 rounded-md border border-primary/40 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
          <PictureInPicture2 className="size-3" /> Aberta em janela
        </div>
      )}
      {(note.tags.length > 0 || note.updatedAt) && (
        <div class="mt-2 flex flex-wrap items-center gap-1 text-[10px]">
          {note.tags.slice(0, 4).map((t) => (
            <span
              key={t}
              class="rounded-md bg-violet-500/10 px-1.5 py-0.5 font-medium text-violet-700 dark:text-violet-300"
            >
              {t}
            </span>
          ))}
          <span class="ml-auto text-muted-foreground">
            {new Date(note.updatedAt).toLocaleDateString("pt-BR")}
          </span>
        </div>
      )}
    </div>
  );
}

// Kebab + popover com Renomear / Compartilhar / Apagar. O popover fecha ao
// clicar fora (overlay) ou em ESC — implementação leve, sem framework de
// popover, pra evitar dependência extra.
function NoteCardMenu({
  open,
  disabled,
  onToggle,
  onClose,
  onRename,
  onShare,
  onPopOut,
  onPdf,
  onDelete
}: {
  open: boolean;
  disabled?: boolean;
  onToggle: () => void;
  onClose: () => void;
  onRename: () => void;
  onShare: () => void;
  onPopOut: () => void;
  onPdf: () => void;
  onDelete: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);
  return (
    <div class="relative shrink-0">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (disabled) return;
          onToggle();
        }}
        disabled={disabled}
        class={`grid size-7 place-items-center rounded-md text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
          disabled
            ? "cursor-not-allowed opacity-40"
            : "hover:bg-accent hover:text-foreground"
        }`}
        title={disabled ? "Anotação aberta em janela" : "Mais opções"}
        aria-label="Mais opções da anotação"
        aria-haspopup="menu"
      >
        <MoreVertical className="size-4" />
      </button>
      {open && !disabled && (
        <>
          <div class="fixed inset-0 z-30" onClick={onClose} aria-hidden />
          <div
            class="absolute right-0 z-40 mt-1 w-48 overflow-hidden rounded-md border border-border bg-card shadow-lg"
            role="menu"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPopOut();
              }}
              class="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-accent"
              role="menuitem"
            >
              <PictureInPicture2 className="size-3.5" /> Abrir em janela
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRename();
              }}
              class="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-accent"
              role="menuitem"
            >
              <Pencil className="size-3.5" /> Renomear
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onShare();
              }}
              class="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-accent"
              role="menuitem"
            >
              <Share2 className="size-3.5" /> Compartilhar
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPdf();
              }}
              class="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-accent"
              role="menuitem"
            >
              <FileDown className="size-3.5" /> Salvar em PDF
            </button>
            <div class="border-t border-border" />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              class="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-destructive hover:bg-destructive/10"
              role="menuitem"
            >
              <Trash2 className="size-3.5" /> Apagar
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** Renderiza o "path" (estilo breadcrumb) onde a nota está. Cada segmento
 *  é clicável. Para questão sem parent-bank resolvido, mostra "Questão"
 *  como segmento estático. */
function NoteContextPath({ note }: { note: HubNote }) {
  const kind = note.targetKind;
  function openContext(e: MouseEvent) {
    e.stopPropagation();
    if (kind === "ai_study_session") {
      activeNav.value = "aistudy";
      activeSessionId.value = note.targetId;
    } else if (kind === "bank") {
      activeNav.value = "bancos";
      openBank(note.targetId);
    }
    // question: sem deeplink direto até existir o vínculo de parent-bank.
  }
  function openSection(e: MouseEvent) {
    e.stopPropagation();
    if (kind === "ai_study_session") {
      activeNav.value = "aistudy";
    } else if (kind === "bank") {
      activeNav.value = "bancos";
    }
  }
  const SectionIcon =
    kind === "ai_study_session"
      ? BookOpen
      : kind === "bank"
        ? Database
        : HelpCircle;
  const sectionLabel =
    kind === "ai_study_session"
      ? "Cadernos"
      : kind === "bank"
        ? "Bancos"
        : "Questão";
  const canDeepLink = kind !== "question" && !!note.targetTitle;
  return (
    <nav
      aria-label="Caminho até esta anotação"
      class="flex flex-wrap items-center gap-0.5 border-b border-border/60 pb-2 text-[11px] text-muted-foreground"
    >
      {kind !== "question" ? (
        <button
          type="button"
          onClick={openSection}
          class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-accent hover:text-foreground"
          title={`Ir para ${sectionLabel}`}
        >
          <SectionIcon className="size-3" />
          {sectionLabel}
        </button>
      ) : (
        <span class="inline-flex items-center gap-1 px-1.5 py-0.5 italic">
          <SectionIcon className="size-3" />
          {sectionLabel}
        </span>
      )}
      {canDeepLink && (
        <>
          <ChevronRight className="size-3 text-muted-foreground/60" />
          <button
            type="button"
            onClick={openContext}
            class="inline-flex max-w-[60%] items-center gap-1 truncate rounded px-1.5 py-0.5 font-semibold text-foreground hover:bg-accent hover:text-primary"
            title={`Abrir ${note.targetTitle ?? sectionLabel.toLowerCase()}`}
          >
            <span class="truncate">{note.targetTitle}</span>
          </button>
        </>
      )}
      {!canDeepLink && kind !== "question" && (
        <>
          <ChevronRight className="size-3 text-muted-foreground/60" />
          <span class="px-1.5 py-0.5 italic text-muted-foreground/70">
            (indisponível)
          </span>
        </>
      )}
    </nav>
  );
}

function kindLabel(kind: HubNote["targetKind"]): string {
  if (kind === "ai_study_session") return "Caderno";
  if (kind === "bank") return "Banco";
  return "Questão";
}
function kindBadge(kind: HubNote["targetKind"]): string {
  if (kind === "ai_study_session")
    return "bg-sky-500/15 text-sky-700 dark:text-sky-300";
  if (kind === "bank")
    return "bg-primary/15 text-primary";
  return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300";
}

function NotePreviewModal({
  note,
  onClose
}: {
  note: HubNote;
  onClose: () => void;
}) {
  function openContext() {
    onClose();
    if (note.targetKind === "ai_study_session") {
      activeNav.value = "aistudy";
      activeSessionId.value = note.targetId;
    } else if (note.targetKind === "bank") {
      activeNav.value = "bancos";
      openBank(note.targetId);
    }
  }

  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div class="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg border border-border bg-card shadow-xl">
        <header class="flex items-start justify-between gap-2 border-b border-border px-5 py-3">
          <div class="min-w-0 flex-1">
            <h2 class="line-clamp-2 text-base font-bold">
              {note.title || "(sem título)"}
            </h2>
            <div class="mt-1 flex flex-wrap items-center gap-1 text-[10px]">
              <span class={`rounded-md px-1.5 py-0.5 font-semibold ${kindBadge(note.targetKind)}`}>
                {kindLabel(note.targetKind)}
              </span>
              {note.tags.map((t) => (
                <span
                  key={t}
                  class="rounded-md bg-violet-500/10 px-1.5 py-0.5 font-medium text-violet-700 dark:text-violet-300"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            class="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </header>
        <div class="flex-1 overflow-y-auto px-5 py-4 text-sm">
          {note.contentMd.trim() ? (
            // Conteúdo é HTML do RichTextEditor (ou markdown legado convertido).
            // dangerouslySetInnerHTML é seguro aqui porque vem da própria conta
            // do aluno — não há vetor de XSS de terceiros.
            <div
              class="prose-question text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: noteContentToHtml(note.contentMd) }}
            />
          ) : (
            <p class="text-muted-foreground italic">Anotação vazia.</p>
          )}
        </div>
        <footer class="flex flex-wrap items-center justify-end gap-2 border-t border-border px-5 py-3">
          <button
            type="button"
            onClick={() =>
              openShareDialog({
                kind: "nota",
                resourceId: note.id,
                resourceTitle: note.title || "(sem título)"
              })
            }
            class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
            title="Compartilhar essa anotação por email"
          >
            <Share2 className="size-3.5 text-violet-500" />
            Compartilhar
          </button>
          <button
            type="button"
            onClick={() => {
              openNoteInWindow(note.id);
              onClose();
            }}
            class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
            title="Abrir essa anotação numa janela dedicada"
          >
            <PictureInPicture2 className="size-3.5 text-primary" />
            Abrir em janela
          </button>
          <span class="flex-1" />
          <button
            type="button"
            onClick={onClose}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
          >
            Fechar
          </button>
          {note.targetKind !== "question" && (
            <button
              type="button"
              onClick={openContext}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:brightness-110"
            >
              <ExternalLink className="size-3.5" />
              Abrir {kindLabel(note.targetKind).toLowerCase()}
              <ArrowRight className="size-3" />
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
